import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import {
    useGetAllPreApprovalsQuery,
    useCreatePreApprovalMutation,
    useUpdatePreApprovalMutation,
    useDeletePreApprovalMutation,
    useChangePreApprovalStatusMutation,
    useGetPreApprovalHistoryQuery,
    useUploadPreApprovalDocumentMutation
} from '../../../../../api';
import { useGetDealerDropdownListQuery } from '../../../../../api/endpoints/dealer';
import { toastService } from '../../../../../utils/toastService';
import { transformPreApprovalToFormData, getPreApprovalsByTab } from '../utils/preApprovalTransformers';
import { convertFiltersToPrimeReactFormat, createInitialFilters } from '../../../../../utils/filterUtils';
import { useGetAllCountriesQuery } from '../../../../../api/endpoints/country';
import { useLanguage } from '../../../../../contexts/LanguageContext';

const usePreApprovalData = () => {
    const { th, getCurrentLanguageId } = useLanguage();
    const [preApprovals, setPreApprovals] = useState([]);
    const [statusCounts, setStatusCounts] = useState({});
    const [loadedStatus, setLoadedStatus] = useState(10);
    const [loading, setLoading] = useState(true);
    const [activeIndex, setActiveIndex] = useState(0);
    const [globalFilterValue, setGlobalFilterValue] = useState('');

    const [selectedCountries, setSelectedCountries] = useState([]);
    const [selectedDealers, setSelectedDealers] = useState({ isAllDealers: false, dealerIds: [] });
    const { data: countryList = [] } = useGetAllCountriesQuery({
        languageId: getCurrentLanguageId()
    });

    const countryOptions = useMemo(() => {
        if (!Array.isArray(countryList)) return [];
        return countryList.map((country) => ({
            label: country.name,
            value: country.name
        }));
    }, [countryList]);

    const filterTypeMap = useMemo(
        () => ({
            startDate: { type: 'date', matchMode: FilterMatchMode.DATE_AFTER },
            endDate: { type: 'date', matchMode: FilterMatchMode.DATE_BEFORE },
            preApprovalNo: { type: 'text', matchMode: FilterMatchMode.STARTS_WITH },
            engineNumber: { type: 'number', matchMode: FilterMatchMode.EQUALS },
            invoiceNo: { type: 'text', matchMode: FilterMatchMode.STARTS_WITH },
            chassisNo: { type: 'text', matchMode: FilterMatchMode.STARTS_WITH },
            serial: { type: 'text', matchMode: FilterMatchMode.STARTS_WITH },
            dealerId: { type: 'multiselect', matchMode: FilterMatchMode.IN },
            countryCode: { type: 'multiselect', matchMode: FilterMatchMode.IN },
            searchText: { type: 'text', matchMode: FilterMatchMode.CONTAINS }
        }),
        []
    );

    const filterFieldNames = useMemo(() => ['startDate', 'endDate', 'chassisNo', 'preApprovalNo', 'serial', 'engineNumber', 'invoiceNo', 'dealerId', 'countryCode', 'searchText'], []);
    const initialFilters = useMemo(() => {
        return createInitialFilters(filterFieldNames, filterTypeMap);
    }, [filterFieldNames, filterTypeMap]);

    const [filters, setFilters] = useState(initialFilters);
    const [apiFilters, setApiFilters] = useState(() => ({
        Tab: 10,
        LanguageId: getCurrentLanguageId()
    }));

    const [dialogVisible, setDialogVisible] = useState(false);
    const [historyDialogVisible, setHistoryDialogVisible] = useState(false);
    const [currentHistoryRecord, setCurrentHistoryRecord] = useState(null);
    const [finalyDescriptionDialogVisible, setFinalyDescriptionDialogVisible] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentPreApproval, setCurrentPreApproval] = useState(null);
    const [submitted, setSubmitted] = useState(false);

    const useDebounce = (callback, delay) => {
        const timeoutRef = useRef(null);
        return useCallback(
            (...args) => {
                if (timeoutRef.current) clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => {
                    callback(...args);
                    timeoutRef.current = null;
                }, delay);
            },
            [callback, delay]
        );
    };

    const tabToStatusMap = useMemo(
        () => ({
            0: 10, // Draft
            1: 20, // WaitingForApproval
            2: 15, // Returned
            3: 30, // Accepted
            4: 40 // Rejected
        }),
        []
    );

    const getFilterValue = (filter) => {
        if (!filter) return null;
        if (filter.value !== undefined) return filter.value;
        if (filter.constraints?.length) return filter.constraints[0]?.value ?? null;
        return null;
    };

    const getPreApprovalPayload = useCallback(
        (currentFilters, currentTabIndex) => {
            const convertedFilters = {};
            convertedFilters.LanguageId = getCurrentLanguageId();

            if (currentFilters.global?.value) {
                convertedFilters.searchTerm = currentFilters.global.value;
            }

            const startDate = getFilterValue(currentFilters.startDate);
            if (startDate) {
                convertedFilters.StartDate = startDate instanceof Date ? startDate.toISOString() : startDate;
            }

            const endDate = getFilterValue(currentFilters.endDate);
            if (endDate) {
                convertedFilters.EndDate = endDate instanceof Date ? endDate.toISOString() : endDate;
            }

            // Tekli text/number filtreler
            const textFilterMapping = {
                searchText: 'SearchText',
                chassisNo: 'ChassisNo',
                preApprovalNo: 'PreApprovalNo',
                serial: 'Serial',
                engineNumber: 'EngineNumber',
                invoiceNo: 'InvoiceNo'
            };

            Object.entries(textFilterMapping).forEach(([jsKey, csharpKey]) => {
                const value = getFilterValue(currentFilters[jsKey]);
                if (value !== null && value !== undefined && value !== '') {
                    convertedFilters[csharpKey] = value;
                }
            });

            const countryCodes = getFilterValue(currentFilters.countryCode);
            if (Array.isArray(countryCodes) && countryCodes.length > 0) {
                convertedFilters.CountryCodes = countryCodes;
            }

            const dealerSelection = getFilterValue(currentFilters.dealerId);
            if (Array.isArray(dealerSelection) && dealerSelection.length > 0) {
                convertedFilters.DealerIds = dealerSelection;
            } else if (dealerSelection?.isAllDealers) {
                convertedFilters.IsAllDealers = true;
            } else if (dealerSelection?.dealerIds?.length > 0) {
                convertedFilters.DealerIds = dealerSelection.dealerIds;
            }

            convertedFilters.Tab = tabToStatusMap[currentTabIndex] || 10;

            return convertedFilters;
        },
        [tabToStatusMap, getCurrentLanguageId]
    );

    // Build API filters including country & dealer multi-select
    const buildApiFilters = useCallback(
        (baseFilters) => {
            const result = { ...baseFilters };

            // Country multi-select filter
            if (selectedCountries && selectedCountries.length > 0) {
                result.CountryCodes = selectedCountries;
            }

            // Dealer multi-select filter
            if (selectedDealers) {
                if (selectedDealers.isAllDealers) {
                    result.IsAllDealers = true;
                } else if (selectedDealers.dealerIds && selectedDealers.dealerIds.length > 0) {
                    result.DealerIds = selectedDealers.dealerIds;
                }
            }

            return result;
        },
        [selectedCountries, selectedDealers]
    );

    const { currentData: apiPreApprovals, isLoading, isFetching, error, refetch } = useGetAllPreApprovalsQuery(apiFilters);

    const [createPreApproval, { isLoading: isCreating }] = useCreatePreApprovalMutation();
    const [updatePreApproval, { isLoading: isUpdating }] = useUpdatePreApprovalMutation();
    const [deletePreApproval, { isLoading: isDeleting }] = useDeletePreApprovalMutation();
    const [changeStatus, { isLoading: isChangingStatus }] = useChangePreApprovalStatusMutation();
    const [uploadDocument] = useUploadPreApprovalDocumentMutation();

    const triggerGetAllPreApprovals = useCallback(() => {
        refetch()
            .then((result) => {
                if (result.error) console.error('❌ API error:', result.error);
            })
            .catch((err) => console.error('❌ Refetch failed:', err));
    }, [refetch]);

    useEffect(() => {
        if (apiPreApprovals) {
            const transformedPreApprovals = getPreApprovalsByTab(apiPreApprovals.items);
            setPreApprovals(transformedPreApprovals);
            setStatusCounts(apiPreApprovals.statusCounts ?? {});
            setLoadedStatus(apiFilters.Tab);
        }
    }, [apiPreApprovals, apiFilters.Tab]);

    useEffect(() => {
        setLoading(isLoading || isFetching || isCreating || isUpdating || isDeleting || isChangingStatus);
    }, [isLoading, isFetching, isCreating, isUpdating, isDeleting, isChangingStatus]);

    useEffect(() => {
        const basePayload = getPreApprovalPayload(filters, activeIndex);
        const fullPayload = buildApiFilters(basePayload);
        setApiFilters(fullPayload);
    }, [filters, activeIndex, getPreApprovalPayload, buildApiFilters]);

    const updateFilters = useCallback((key, value) => {
        setFilters((prevFilters) => {
            if (key === 'global') {
                const newGlobal = { ...prevFilters.global, value };
                if (JSON.stringify(newGlobal) === JSON.stringify(prevFilters.global)) return prevFilters;
                return { ...prevFilters, global: newGlobal };
            } else {
                const currentFilter = prevFilters[key];
                if (!currentFilter) return prevFilters;
                if (key === 'isActive') {
                    const newFilter = { value, matchMode: FilterMatchMode.EQUALS };
                    if (JSON.stringify(newFilter) === JSON.stringify(currentFilter)) return prevFilters;
                    return { ...prevFilters, [key]: newFilter };
                } else {
                    const newConstraints = [{ value, matchMode: FilterMatchMode.STARTS_WITH }];
                    const newFilter = { operator: FilterOperator.AND, constraints: newConstraints };
                    if (JSON.stringify(newFilter) === JSON.stringify(currentFilter)) return prevFilters;
                    return { ...prevFilters, [key]: newFilter };
                }
            }
        });
    }, []);

    const debouncedUpdateFilters = useDebounce(updateFilters, 300);

    const onGlobalFilterChange = useCallback(
        (e) => {
            const value = e.target.value;
            setGlobalFilterValue(value);
            debouncedUpdateFilters('global', value);
        },
        [debouncedUpdateFilters]
    );

    const handleDelete = async (id) => {
        try {
            await deletePreApproval(id).unwrap();
            toastService.success(th('preApproval.messages.deleteSuccess'));
            refetch();
        } catch (error) {
            toastService.error(th('preApproval.messages.deleteError'));
        }
    };

    const savePreApproval = async (preApprovalData, isEdit, currentPreApproval, skipAutoRefetch = false, files = []) => {
        try {
            let createdId = null;
            if (isEdit && currentPreApproval) {
                await updatePreApproval(preApprovalData).unwrap();
                toastService.success(th('preApproval.messages.saveSuccess'));
            } else {
                const response = await createPreApproval(preApprovalData).unwrap();
                createdId = response?.id || response?.data?.id || response;

                // Upload multiple files for new record
                if (files.length > 0 && createdId) {
                    let uploadSuccess = 0;
                    let uploadFail = 0;

                    for (const file of files) {
                        try {
                            await uploadDocument({
                                PreapprovalFormId: createdId,
                                file: file,
                                description: 'Ön Onay Dosyası'
                            }).unwrap();
                            uploadSuccess++;
                        } catch (fileError) {
                            console.error('Dosya yükleme hatası:', fileError);
                            uploadFail++;
                        }
                    }

                    if (uploadFail > 0) {
                        toastService.warning(th('preApproval.fileOperations.uploadMultiPartial').replace('{0}', uploadSuccess).replace('{1}', uploadFail));
                    } else if (uploadSuccess > 0) {
                        toastService.success(th('preApproval.fileOperations.uploadSuccess').replace('{0}', uploadSuccess));
                    }
                }

                toastService.success(th('preApproval.messages.createSuccess'));
            }

            if (!skipAutoRefetch) refetch();
            return { success: true, id: createdId };
        } catch (error) {
            console.error('Save pre-approval error:', error);
            return { success: false, id: null };
        }
    };

    const changePreApprovalStatus = async (id, newStatus, note = '') => {
        try {
            await changeStatus({ id, newStatus, note }).unwrap();
            toastService.success(th('preApproval.messages.statusUpdateSuccess'));
            refetch();
            return true;
        } catch (error) {
            toastService.error(th('preApproval.messages.statusUpdateError'));
            return false;
        }
    };

    const { data: dealerList = [] } = useGetDealerDropdownListQuery({
        RowCount: 100,
        SearchText: ''
    });

    const dealerOptions = useMemo(() => {
        return dealerList.map((dealer) => ({
            label: dealer.name,
            value: dealer.dealerId
        }));
    }, [dealerList]);

    const filterFields = useMemo(
        () => [
            {
                field: 'startDate',
                headerName: th('preApproval.filters.startDate'),
                type: 'date'
            },
            {
                field: 'endDate',
                headerName: th('preApproval.filters.endDate'),
                type: 'date'
            },
            {
                field: 'countryCode',
                headerName: th('preApproval.filters.country') || 'Ülke',
                type: 'multiselect',
                options: countryOptions
            },
            {
                field: 'dealerId',
                headerName: th('preApproval.filters.dealer') || 'Bayi',
                type: 'multiselect',
                options: dealerOptions
            },
            {
                field: 'chassisNo',
                headerName: th('preApproval.filters.chassisNo'),
                type: 'text'
            },
            {
                field: 'preApprovalNo',
                headerName: th('preApproval.filters.preApprovalNo'),
                type: 'text'
            },
            {
                field: 'serial',
                headerName: th('preApproval.filters.serial'),
                type: 'text'
            },
            {
                field: 'engineNumber',
                headerName: th('preApproval.filters.engineNumber'),
                type: 'number'
            },
            {
                field: 'invoiceNo',
                headerName: th('preApproval.filters.invoiceNo'),
                type: 'text'
            },
            {
                field: 'searchText',
                headerName: th('preApproval.filters.searchText') || 'Arama Metni',
                type: 'text',
                filterPlaceholder: th('preApproval.filters.searchTextPlaceholder') || 'Müşteri beyanı / arıza nedeni',
            }
        ],
        [th, dealerOptions, countryOptions]
    );

    const handleSearch = useCallback(
        (searchValue) => {
            setGlobalFilterValue(searchValue);
            debouncedUpdateFilters('global', searchValue);
        },
        [debouncedUpdateFilters]
    );

    const handleFilterChange = useCallback(
        (updatedFilters) => {
            const convertedFilters = convertFiltersToPrimeReactFormat(updatedFilters, filters, filterTypeMap);
            setFilters(convertedFilters);
            const apiParams = {};
            Object.entries(updatedFilters).forEach(([key, value]) => {
                if (value) {
                    apiParams[key] = key.includes('Date') && value instanceof Date ? value.toISOString() : value;
                }
            });
        },
        [filters, filterTypeMap]
    );

    const handleResetFilters = useCallback(() => {
        const resetFilters = createInitialFilters(filterFieldNames, filterTypeMap);
        Object.keys(resetFilters).forEach((key) => {
            if (key !== 'global') {
                const filterType = filterTypeMap[key]?.type || 'text';
                switch (filterType) {
                    case 'boolean':
                    case 'autoComplete':
                        resetFilters[key] = { value: null, matchMode: FilterMatchMode.EQUALS };
                        break;
                    default:
                        resetFilters[key] = {
                            operator: FilterOperator.AND,
                            constraints: [{ value: null, matchMode: filterType === 'text' ? FilterMatchMode.STARTS_WITH : FilterMatchMode.EQUALS }]
                        };
                        break;
                }
            }
        });
        setFilters(resetFilters);

        // Reset country & dealer multi-select
        setSelectedCountries([]);
        setSelectedDealers({ isAllDealers: false, dealerIds: [] });

        setApiFilters((prev) => {
            const { Tab } = prev;
            return {
                Tab,
                SearchText: undefined,
                ChassisNo: undefined,
                PreApprovalNo: undefined,
                Serial: undefined,
                EngineNumber: undefined,
                InvoiceNo: undefined,
                DealerIds: undefined,
                CountryCodes: undefined
            };
        });
    }, [filterFieldNames, filterTypeMap]);

    const handleApplyFilters = useCallback(
        (updatedFilters) => {
            const convertedFilters = convertFiltersToPrimeReactFormat(updatedFilters, filters, filterTypeMap);
            setFilters(convertedFilters);
            const apiParams = {};
            Object.entries(updatedFilters).forEach(([key, value]) => {
                if (value) {
                    apiParams[key] = key.includes('Date') && value instanceof Date ? value.toISOString() : value;
                }
            });
        },
        [filters, filterTypeMap]
    );

    // Country filter change handler
    const handleCountriesChange = useCallback((countries) => {
        setSelectedCountries(countries);
    }, []);

    // Dealer multi-select change handler
    const handleDealersChange = useCallback((dealerSelection) => {
        setSelectedDealers(dealerSelection);
    }, []);

    const handleAddNew = useCallback(() => {
        setEditMode(false);
        setCurrentPreApproval(null);
        setSubmitted(false);
        setDialogVisible(true);
    }, []);

    const handleEdit = useCallback((preApproval) => {
        setEditMode(true);
        setCurrentPreApproval(preApproval);
        setSubmitted(false);
        setDialogVisible(true);
    }, []);

    const hideDialog = useCallback(() => setDialogVisible(false), []);
    const hideFinalyDescriptionDialog = useCallback(() => setFinalyDescriptionDialogVisible(false), []);
    const hideHistoryDialog = useCallback(() => {
        setHistoryDialogVisible(false);
        setCurrentHistoryRecord(null);
    }, []);
    const showHistoryDialog = useCallback((record) => {
        setCurrentHistoryRecord(record);
        setHistoryDialogVisible(true);
    }, []);

    return {
        preApprovals,
        statusCounts,
        loadedStatus,
        loading,
        filters,
        globalFilterValue,
        activeIndex,
        error,

        isCreating,
        isUpdating,
        isDeleting,
        isChangingStatus,

        dialogVisible,
        historyDialogVisible,
        finalyDescriptionDialogVisible,
        editMode,
        currentPreApproval,
        currentHistoryRecord,
        submitted,

        filterFields,
        customFilterOptions: {
            dealerId: dealerOptions,
            countryCode: countryOptions
        },
        customFilterTypes: {
            dealerId: 'multiselect',
            countryCode: 'multiselect'
        },

        selectedCountries,
        selectedDealers,
        handleCountriesChange,
        handleDealersChange,

        handleDelete,
        savePreApproval,
        changePreApprovalStatus,
        handleSearch,
        handleFilterChange,
        handleResetFilters,
        handleApplyFilters,
        onGlobalFilterChange,
        triggerGetAllPreApprovals,

        handleAddNew,
        handleEdit,
        hideDialog,
        hideFinalyDescriptionDialog,
        hideHistoryDialog,
        showHistoryDialog,
        setFinalyDescriptionDialogVisible,

        setActiveIndex: (newIndex) => {
            setActiveIndex(newIndex);
        },

        setSubmitted,
        refetch
    };
};

export default usePreApprovalData;
